import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { TrimWhitespaceDirective } from '../../../../core/directives/trim.directive';
import { PhoneFormatDirective } from '../../../../core/directives/phone-format.directive';
import { trimFormGroup } from '../../../../core/utils/form.utils';
import { ApiService } from '../../../../core/services/api.service';
import { ToastService } from '../../../../core/services/toast.service';
import { S3ImageUploadService } from '../../../../core/services/s3-image-upload.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { AddFamilyMemberRequest, FamilyMemberDetail, FamilyMemberItem, UpdateFamilyMemberRequest } from '../../../../core/models/loved-ones.models';
import { MasterListData, RelationshipItem } from '../../../../core/models/story.models';

@Component({
  selector: 'app-add-family-member',
  templateUrl: './add-family-member.html',
  styleUrl: './add-family-member.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TrimWhitespaceDirective, PhoneFormatDirective, KENDO_DROPDOWNS, NgTemplateOutlet],
})
export class AddFamilyMember implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly s3 = inject(S3ImageUploadService);

  /** Family space ID passed from parent. */
  readonly familySpaceId = input.required<string>();

  /** Member to edit — null for add mode. */
  readonly editMember = input<FamilyMemberItem | null>(null);
  /** When true renders as an inline expand panel instead of a modal. */
  readonly isInline = input(false);

  /** Emits when member is successfully added/updated. */
  readonly memberAdded = output<void>();
  /** Emits when user cancels / closes. */
  readonly closed = output<void>();

  readonly isLoading = signal(false);
  readonly isPhotoUploading = signal(false);
  readonly photoPreview = signal<string | null>(null);
  /** True while fetching the full member detail for edit mode. */
  readonly isFetchingDetail = signal(false);
  private photoS3Key = '';

  readonly relationshipOptions = signal<RelationshipItem[]>([]);

  readonly memberForm = this.fb.group({
    firstName:    ['', [Validators.required, Validators.minLength(2)]],
    lastName:     ['', [Validators.required, Validators.minLength(2)]],
    relationship: [null as number | null, [Validators.required]],
    email:        ['', [Validators.required, Validators.email]],
    phoneNumber:  [''],
  });

  get f() { return this.memberForm.controls; }

  constructor() {
    effect(() => {
      const member = this.editMember();
      untracked(() => {
        this.memberForm.reset();
        this.photoPreview.set(null);
        this.photoS3Key = '';
        if (member) {
          this.memberForm.patchValue({
            firstName:   member.fullName?.split(' ')[0] ?? '',
            lastName:    member.fullName?.split(' ').slice(1).join(' ') ?? '',
            email:       member.email       ?? '',
            phoneNumber: member.phoneNumber ?? '',
          });
          if (member.profilePhotoUrl) {
            this.photoPreview.set(member.profilePhotoUrl);
          }
          this.loadMemberDetail(member.familyMemberId, this.familySpaceId());
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadRelationships();
  }

  /** Fetches the full member detail and updates the relationship dropdown for edit mode. */
  private async loadMemberDetail(familyMemberId: string, familySpaceId: string): Promise<void> {
    this.isFetchingDetail.set(true);
    try {
      const resp = await this.api.request<FamilyMemberDetail>(
        'GET',
        `${API_ROUTES.GET_FAMILY_MEMBER_DETAIL_BY_ID}/${familyMemberId}/${familySpaceId}`,
        undefined,
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data) {
        const d = resp.data;
        this.memberForm.patchValue({
          firstName:    d.firstName         ?? '',
          lastName:     d.lastName          ?? '',
          email:        d.email             ?? '',
          phoneNumber:  d.phoneNumber       ?? '',
          relationship: d.relationshipTypeId ?? null,
        });
        if (d.profilePhotoUrl) {
          this.photoPreview.set(d.profilePhotoUrl);
        }
      }
    } catch { /* silent — form retains the basic data already patched */ }
    finally {
      this.isFetchingDetail.set(false);
    }
  }

  private async loadRelationships(): Promise<void> {
    try {
      const resp = await this.api.request<MasterListData>(
        'POST',
        API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
        { typeIds: [4] },
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data?.relationshipList?.length) {
        this.relationshipOptions.set(resp.data.relationshipList);
      }
    } catch { /* silent — dropdown stays empty */ }
  }

  async onPhotoSelected(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.isPhotoUploading.set(true);
    try {
      const result = await this.s3.upload(file, 'memberPhoto');
      this.photoS3Key = result.fileName;
      this.photoPreview.set(result.url);
    } catch {
      this.toast.error('Failed to upload photo. Please try again.');
    } finally {
      this.isPhotoUploading.set(false);
    }
  }

  removePhoto(): void {
    this.photoS3Key = '';
    this.photoPreview.set(null);
  }

  async onSubmit(): Promise<void> {
    trimFormGroup(this.memberForm);
    if (this.memberForm.invalid) {
      this.memberForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const { firstName, lastName, relationship, email, phoneNumber } = this.memberForm.value;
    const member = this.editMember();

    let resp;
    if (member) {
      const body: UpdateFamilyMemberRequest = {
        familyMemberId:       member.familyMemberId,
        firstName:            firstName!,
        lastName:             lastName!,
        email:                email!,
        phoneNumber:          phoneNumber!,
        profilePhotoFileName: this.photoS3Key || '',
        relationshipTypeId:   relationship!,
        familySpaceId:        this.familySpaceId(),
      };
      resp = await this.api.request(
        'POST',
        API_ROUTES.UPDATE_FAMILY_MEMBER,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
    } else {
      const body: AddFamilyMemberRequest = {
        firstName:            firstName!,
        lastName:             lastName!,
        email:                email!,
        phoneNumber:          phoneNumber!,
        profilePhotoFileName: this.photoS3Key || '',
        relationshipTypeId:   relationship!,
        familySpaceId:        this.familySpaceId(),
      };
      resp = await this.api.request(
        'POST',
        API_ROUTES.ADD_FAMILY_MEMBER,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
    }

    if (resp.status) {
      this.memberAdded.emit();
    }
    this.isLoading.set(false);
  }
}
